
import { ProductAttribute } from "./attributes";

// Base interface for recurring products
export interface RecurringProductBase {
  readonly id: string;
  readonly user_id: string;
  readonly product_id: string;
  readonly frequency_days: number;
  readonly recurrence_group?: string;
  readonly last_purchase_date?: string;
  readonly next_purchase_date?: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly attributes?: ProductAttribute[];
  
  // Denormalized product data for offline use
  readonly product_name?: string;
  readonly product_default_quantity?: number;
  readonly product_default_unit?: string;
  readonly category_name?: string;
  readonly category_icon?: string;
}

// Database representation (what comes from Supabase)
export interface RecurringProductDB extends RecurringProductBase {
  readonly active: boolean;
}

// Enhanced representation (with computed fields)
export interface RecurringProductEnhanced extends RecurringProductBase {
  readonly status: 'active' | 'inactive' | 'paused';
  readonly days_until_next?: number;
  readonly is_overdue?: boolean;
  readonly purchase_frequency_text?: string;
  /** Next purchase date adjusted to always be in the future */
  readonly effective_next_purchase_date?: string;
}

// Form data for creating/updating
export interface RecurringProductFormData {
  productId: string;
  frequencyDays: number;
  recurrenceGroup?: string;
  attributes?: ProductAttribute[];
  nextPurchaseDate?: string;
}

export interface RecurringProductUpdateData {
  frequency_days?: number;
  recurrence_group?: string;
  last_purchase_date?: string;
  next_purchase_date?: string;
  status?: 'active' | 'inactive' | 'paused';
  attributes?: ProductAttribute[];
}

// Legacy type for backward compatibility - will be deprecated
export interface RecurringProduct extends RecurringProductDB {
  product?: {
    name: string;
    default_quantity: number;
    default_unit: string;
    category?: {
      name: string;
      icon: string;
    }
  };
}

// Additional types for backward compatibility with recurring-enhanced.ts
export type DataState<T = any> = 
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'empty' }
  | { status: 'success'; data: T };

export type SyncStatus = 'pending' | 'synced' | 'deleted' | 'error';

export interface OptimizedQueryOptions {
  staleTime: number;
  cacheTime: number;
  refetchOnWindowFocus: boolean;
  refetchOnReconnect: boolean;
  retry: number | boolean;
  enabled?: boolean;
  structuralSharing?: boolean;
}

// Conversion utilities
export const convertDBToEnhanced = (db: RecurringProductDB): RecurringProductEnhanced => {
  const status: 'active' | 'inactive' | 'paused' = db.active ? 'active' : 'inactive';

  // Compute an effective next purchase date always in the future
  let effectiveNext: Date | undefined;
  if (db.next_purchase_date) {
    effectiveNext = new Date(db.next_purchase_date);
    if (db.frequency_days > 0) {
      const now = new Date();
      while (effectiveNext < now) {
        effectiveNext = new Date(effectiveNext.getTime() + db.frequency_days * 24 * 60 * 60 * 1000);
      }
    }
  }

  const days_until_next = effectiveNext
    ? Math.ceil((effectiveNext.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : undefined;

  const is_overdue = db.next_purchase_date
    ? new Date(db.next_purchase_date) < new Date()
    : false;
    
  const purchase_frequency_text = `Ogni ${db.frequency_days} giorn${db.frequency_days === 1 ? 'o' : 'i'}`;

  return {
    ...db,
    status,
    days_until_next,
    is_overdue,
    purchase_frequency_text,
    effective_next_purchase_date: effectiveNext ? effectiveNext.toISOString() : undefined
  };
};

export const convertEnhancedToDB = (enhanced: RecurringProductEnhanced): RecurringProductDB => {
  const {
    status,
    days_until_next,
    is_overdue,
    purchase_frequency_text,
    effective_next_purchase_date,
    ...rest
  } = enhanced;
  return {
    ...rest,
    active: status === 'active'
  };
};

export const convertEnhancedToLegacy = (enhanced: RecurringProductEnhanced): RecurringProduct => {
  return {
    ...convertEnhancedToDB(enhanced),
    product: enhanced.product_name ? {
      name: enhanced.product_name,
      default_quantity: enhanced.product_default_quantity || 1,
      default_unit: enhanced.product_default_unit || 'pz',
      category: enhanced.category_name
        ? {
            name: enhanced.category_name,
            icon: enhanced.category_icon || ''
          }
        : undefined
    } : undefined
  };
};
